import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useMemo, useState, useRef, useCallback, useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import DataCacheService from '@/shared/services/DataCacheService';
import { listItemMsg } from '@/shared/components/FormattedListValue';
const messages = defineMessages({
    ShowAll: { id: 'Show all', defaultMessage: 'Show all' },
});
const CountryFlag = ({ code }) => (_jsx("div", { className: `iti__flag${code ? ` iti__${code.toLowerCase()}` : ''}`, style: { display: 'inline-block' } }));
const TYPEAHEAD_LIMIT = 10;
const CountrySelect = ({ value, onChange, placeholder, disabled = false, }) => {
    const intl = useIntl();
    const [inputValue, setInputValue] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [showAll, setShowAll] = useState(false);
    const [isTyping, setIsTyping] = useState(false);
    const containerRef = useRef(null);
    const allCountries = useMemo(() => {
        var _a;
        const countries = ((_a = DataCacheService.getDataCache()) === null || _a === void 0 ? void 0 : _a.country) || [];
        return countries.map((c) => ({
            key: c.code,
            label: intl.formatMessage(listItemMsg('country', c.code, c.name)),
            isCommon: c.isCommon,
        }));
    }, [intl]);
    const commonCountries = useMemo(() => allCountries.filter((c) => c.isCommon).sort((a, b) => a.label.localeCompare(b.label)), [allCountries]);
    const allCountriesSorted = useMemo(() => [...allCountries].sort((a, b) => a.label.localeCompare(b.label)), [allCountries]);
    const selectedCountry = value ? allCountries.find((c) => c.key === value) || null : null;
    useEffect(() => {
        if (!isTyping) {
            setInputValue(selectedCountry ? selectedCountry.label : '');
        }
    }, [selectedCountry, isTyping]);
    const typeaheadResults = useMemo(() => {
        if (!inputValue)
            return [];
        const search = inputValue.toUpperCase();
        return allCountries
            .filter((c) => c.label.toUpperCase().includes(search))
            .sort((a, b) => {
            if (a.isCommon !== b.isCommon)
                return a.isCommon ? -1 : 1;
            return a.label.localeCompare(b.label);
        })
            .slice(0, TYPEAHEAD_LIMIT);
    }, [inputValue, allCountries]);
    const selectItem = useCallback((item) => {
        onChange(item.key);
        setInputValue(item.label);
        setIsOpen(false);
        setIsTyping(false);
        setShowAll(false);
    }, [onChange]);
    const handleInputChange = useCallback((e) => {
        setInputValue(e.target.value);
        setIsTyping(true);
        setIsOpen(true);
    }, []);
    const handleInputFocus = useCallback(() => {
        if (selectedCountry) {
            setInputValue(selectedCountry.label);
        }
    }, [selectedCountry]);
    const handleBlur = useCallback((e) => {
        var _a;
        if ((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(e.relatedTarget))
            return;
        setIsOpen(false);
        setIsTyping(false);
        setShowAll(false);
        if (!inputValue) {
            onChange(null);
            return;
        }
        const match = allCountries.find((c) => c.label.toUpperCase() === inputValue.toUpperCase());
        if (match) {
            selectItem(match);
        }
        else if (selectedCountry) {
            setInputValue(selectedCountry.label);
        }
        else {
            onChange(null);
            setInputValue('');
        }
    }, [inputValue, allCountries, selectedCountry, onChange, selectItem]);
    const toggleDropdown = useCallback(() => {
        if (disabled)
            return;
        setIsTyping(false);
        setIsOpen((prev) => {
            if (!prev)
                setShowAll(false);
            return !prev;
        });
    }, [disabled]);
    const handleShowAll = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        setShowAll(true);
    }, []);
    const renderItem = (item) => (_jsx("li", Object.assign({ className: (selectedCountry === null || selectedCountry === void 0 ? void 0 : selectedCountry.key) === item.key ? 'selected' : undefined }, { children: _jsxs("a", Object.assign({ href: "#", onMouseDown: (e) => {
                e.preventDefault();
                selectItem(item);
            } }, { children: [_jsx("div", Object.assign({ className: "flag-box" }, { children: _jsx(CountryFlag, { code: item.key }) })), _jsx("span", Object.assign({ className: "country-name" }, { children: item.label }))] })) }), item.key));
    const defaultPlaceholder = intl.formatMessage(listItemMsg('country', null, ''));
    const renderDropdownContent = () => {
        if (isTyping) {
            if (typeaheadResults.length === 0)
                return null;
            return typeaheadResults.map(renderItem);
        }
        return (_jsxs(_Fragment, { children: [commonCountries.map((item) => renderItem(item)), _jsx("li", { role: "separator", className: "divider" }), showAll ? (allCountriesSorted.map(renderItem)) : (_jsx("li", { children: _jsx("a", Object.assign({ href: "#", onMouseDown: handleShowAll }, { children: _jsx("span", { children: intl.formatMessage(messages.ShowAll) }) })) }))] }));
    };
    return (_jsxs("div", Object.assign({ ref: containerRef, className: "dropdown", onBlur: handleBlur }, { children: [_jsxs("div", Object.assign({ className: "input-group" }, { children: [_jsx("span", Object.assign({ className: "input-group-addon", style: { lineHeight: 1 } }, { children: _jsx(CountryFlag, { code: value }) })), _jsx("input", { type: "text", className: "form-control", value: inputValue, onChange: handleInputChange, onFocus: handleInputFocus, placeholder: placeholder || defaultPlaceholder, disabled: disabled, autoComplete: "off" }), _jsx("span", Object.assign({ className: "input-group-btn" }, { children: _jsx("button", Object.assign({ type: "button", className: "btn btn-default dropdown-toggle btn-font-14", onClick: toggleDropdown, disabled: disabled, tabIndex: -1 }, { children: _jsx("span", { className: "caret" }) })) }))] })), isOpen && (_jsx("ul", Object.assign({ className: "dropdown-menu", role: "menu", style: { display: 'block', maxHeight: 200, overflowY: 'auto', width: '100%' } }, { children: renderDropdownContent() })))] })));
};
export default CountrySelect;
